import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import axios from "../../../utils/axiosInstance";
import { toast } from "react-toastify";

export default function AssignDevice({
  handleRefresh,
  device,
  disabled = false,
  open,
  onClose,
  onAddTechnician,
  group,
  regionRefresher,
}) {
  const [groupName, setGroupName] = useState(group.name);
  const [selectedRegionDistrict, setSelectedRegionDistrict] = useState("");
  const [routeName, setRouteName] = useState("");
  const [allRoutes, setAllRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState("");
  
  // Validation errors state
  const [errors, setErrors] = useState({});
  const [allRegions, setAllRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);

   useEffect(() => {
    if (open && device) {
      setSelectedRegionDistrict(device.regionId || "");
      setSelectedRoute(device.technicianId || "");
    }
  }, [open, device]);


  // 🔹 Update group name when group prop changes
  useEffect(() => {
    if (group?.name) {
      setGroupName(group.name);
    }
  }, [group]);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setLoadingRegions(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/regions?groupId=${group.id}`,
          {
            params: { companyId: process.env.REACT_APP_COMPANY_ID },
          }
        );
        setAllRegions(response.data);
      } catch (err) {
        console.error("❌ Error fetching regions:", err);
        setAllRegions([]);
      } finally {
        setLoadingRegions(false);
      }
    };

    if (group?.id) {
      fetchRegions();
    }
  }, [groupName, regionRefresher, group?.id]);

  useEffect(() => {
    if (!selectedRegionDistrict) {
      setAllRoutes([]);
      setSelectedRoute("");
      return;
    }
    
    const fetchRoutes = async () => {
      try {
        setLoadingRoutes(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/routes?regionId=${selectedRegionDistrict}`
        );
        setAllRoutes(response.data);
      } catch (err) {
        console.error("❌ Error fetching routes:", err);
        setAllRoutes([]);
      } finally {
        setLoadingRoutes(false);
      }
    };

    fetchRoutes();
  }, [selectedRegionDistrict]);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!groupName) {
      newErrors.groupName = "Group Name is required";
      toast.error("Group Name is required");
    }
    if (!selectedRegionDistrict) {
      newErrors.region = "Region is required";
      toast.error("Region is required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAssign = async () => {
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    try {
      const payload = {
        regionId: selectedRegionDistrict,
        routeId: selectedRoute,
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/assets?deviceId=${device.id}&groupId=${group.id}`,
        payload
      );

      if (onAddTechnician) {
        onAddTechnician(response.data);
      }

      toast.success("Device assigned successfully!");
      console.log("✅ Device assigned:", response.data);
    } catch (error) {
      console.error(
        "❌ Error assigning device:",
        error.response?.data || error.message
      );
      toast.error("Failed to assign device");
    } finally {
      onClose();
      resetForm();
    }
  };

  // Clear errors when user starts typing
  const handleFieldChange = (field, value, setter) => {
    setter(value);
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };
  
  const resetForm = () => {
    setRouteName("");
    setSelectedRegionDistrict("");
    setSelectedRoute("");
    setErrors({});
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Device</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* Group Name - Required */}
          <TextField
           sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
            label="Parent Group"
            required
            disabled
            value={groupName}
            onChange={(e) =>
              handleFieldChange("groupName", e.target.value, setGroupName)
            }
            fullWidth
            error={!!errors.groupName}
            helperText={errors.groupName}
          />

          {/* Region/District - Required */}
          <TextField
           sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
            select
            label="Region/District"
            required
            value={selectedRegionDistrict}
            onChange={(e) => {
              setSelectedRegionDistrict(e.target.value);
              setSelectedRoute(""); // Reset technician when region changes
            }}
            fullWidth
            error={!!errors.region}
            helperText={errors.region}
          >
            {allRegions.length > 0 ? (
              allRegions.map((regionItem) => (
                <MenuItem key={regionItem.id} value={regionItem.id}>
                  {regionItem.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingRegions
                  ? "Loading Regions..."
                  : groupName
                  ? "No Regions available"
                  : "Select Group Name first"}
              </MenuItem>
            )}
          </TextField>

          {/* Technician - Optional */}
          <TextField
            select
            label="Technician"
            value={selectedRoute}
            onChange={(e) => setSelectedRoute(e.target.value)}
            fullWidth
            displayEmpty
            disabled={disabled || loadingRoutes || !selectedRegionDistrict}
            variant="outlined"
            sx={{        
              "& .MuiOutlinedInput-root": {
                borderRadius: 0, 
                backgroundColor: "transparent",
                color: "#fff",
                "& fieldset": { borderColor: "#9ca3af" },
                "&:hover fieldset": { borderColor: "#9ca3af" },
                "&.Mui-focused fieldset": { borderColor: "#6b7280" },
              },
              "& .MuiInputLabel-root": { color: "#9ca3af" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#6b7280" },
            }}
          >
            <MenuItem value="">No Route Assigned</MenuItem>
            {allRoutes.length > 0 ? (
              allRoutes.map((routeItem) => (
                <MenuItem key={routeItem.id} value={routeItem.id}>
                  {routeItem.name ||
                    `${routeItem.firstName} ${routeItem.lastName}` ||
                    routeItem.email}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingRoutes ? "Loading routes..." : "No routes available"}
              </MenuItem>
            )}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="error">
          Cancel
        </Button>
        <Button
          sx={{
                  borderRadius: 0, // ✅ remove rounding
              }}
               
          onClick={handleAssign}
          variant="contained"
          color="primary"
          disabled={!groupName || !selectedRegionDistrict}
        >
          Assign Device
        </Button>
      </DialogActions>
    </Dialog>
  );
}